import { Link } from "react-router-dom";

export type LeadRow = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  event_type: string | null;
  event_date: string | null;
  status: string;
  created_at: string;
};

interface Props {
  leads: LeadRow[];
}

const STATUS_STYLES: Record<string, string> = {
  new:        "bg-[#C8A96E]/15 text-[#a6853e]",
  contacted:  "bg-teal-100 text-teal-700",
  booked:     "bg-emerald-100 text-emerald-700",
  lost:       "bg-slate-100 text-slate-400",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DashboardLeads({ leads }: Props) {
  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 text-[#a6853e]">
            <i className="ri-user-received-line text-xs"></i>
          </span>
          <h3 className="text-[#0f172a] font-semibold text-sm">Recent Leads</h3>
          {newCount > 0 && (
            <span className="bg-[#C8A96E]/15 text-[#a6853e] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {newCount} new
            </span>
          )}
        </div>
        <Link
          to="/admin/leads"
          className="text-[#C8A96E] hover:text-[#a6853e] text-xs transition-colors cursor-pointer whitespace-nowrap"
        >
          View all →
        </Link>
      </div>

      {/* List */}
      <div className="flex-1 divide-y divide-slate-100">
        {leads.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-300">
              <i className="ri-user-received-line text-lg"></i>
            </span>
            <p className="text-slate-400 text-sm">No leads yet</p>
            <p className="text-slate-300 text-xs">Leads from your public page will appear here</p>
          </div>
        ) : (
          leads.map((lead) => (
            <Link
              key={lead.id}
              to={`/admin/leads/${lead.id}`}
              className="px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-[#C8A96E]/15 flex items-center justify-center shrink-0">
                <span className="text-[#a6853e] text-xs font-bold">
                  {lead.name.charAt(0).toUpperCase()}
                </span>
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[#0f172a] text-sm font-medium truncate">{lead.name}</p>
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize shrink-0 ${
                      STATUS_STYLES[lead.status] ?? "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {lead.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {lead.event_type && (
                    <span className="text-slate-500 text-xs">{lead.event_type}</span>
                  )}
                  {lead.event_date && (
                    <span className="text-slate-400 text-xs">· {lead.event_date}</span>
                  )}
                </div>
              </div>
              {/* Time */}
              <span className="text-slate-400 text-xs whitespace-nowrap shrink-0">{timeAgo(lead.created_at)}</span>
            </Link>
          ))
        )}
      </div>

      {/* Footer CTA */}
      {leads.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-100 shrink-0">
          <Link
            to="/admin/leads"
            className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium text-slate-500 hover:text-[#0f172a] hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            Manage all leads
          </Link>
        </div>
      )}
    </div>
  );
}
